import { CandidateWorkExperience, ProfileEducation } from '@nxtpeople/nxt-styles';
import dayjs from 'dayjs';
import i18n from '../../../core/i18n';
import { Candidate } from './../../../core/collections/candidates/candidate.interface';

export function candidateMapper(candidate: Candidate): Candidate {
   try {
      return {
         ...candidate,

         profile: {
            ...candidate?.profile,
            educations:
               [...(candidate?.profile?.educations || [])]
                  ?.filter((education, index, self) => self.findIndex((e) => e.sync_id === education.sync_id) === index)
                  .map(educationMapper)
                  .sort((a, b) => {
                     const aFrom = dayjs(a.from, 'YYYY-MM-DD');
                     const bFrom = dayjs(b.from, 'YYYY-MM-DD');
                     return bFrom.isAfter(aFrom) ? 1 : -1; // Sort descending by 'from' date
                  }) || [],
         },
         work_experiences:
            candidate?.work_experiences
               ?.filter((experience, index, self) => self.findIndex((e) => e.sync_id === experience.sync_id) === index)
               .map(workExperienceMapper)
               .sort((a, b) => {
                  const aFrom = dayjs(a.from, 'YYYY-MM-DD');
                  const bFrom = dayjs(b.from, 'YYYY-MM-DD');
                  return bFrom.isAfter(aFrom) ? 1 : -1; // Sort descending by 'from' date
               }) || [],
      };
   } catch (error: any) {
      throw Error(error);
   }
}

export function workExperienceMapper(workExperience: CandidateWorkExperience): any {
   if (!workExperience.from) return workExperience;

   const tillDate = workExperience.till ? dayjs(workExperience.till, 'YYYY-MM-DD') : dayjs();
   const diffObj = dayjs.duration(dayjs(tillDate).diff(dayjs(workExperience.from, 'YYYY-MM-DD')));

   const years = diffObj.years();

   const totalMonths = diffObj.asMonths();
   const months = Math.floor(totalMonths - years * 12); // 12 months per year

   const totalWeeks = diffObj.asWeeks();
   const weeks = Math.floor(totalWeeks - Math.floor(totalMonths) * 4); // Approximate 4 weeks per month

   const totalDays = diffObj.asDays();
   const days = Math.floor(totalDays - Math.floor(totalWeeks) * 7); // 7 days per week

   let diff = '';

   if (years > 0) {
      diff += `${years}${i18n.global.t('generic.dates.year', 2)} `;
   }

   const nonZeroUnitsMonth = [years].filter((unit) => unit > 0).length;
   if (months > 0 && (nonZeroUnitsMonth === 0 || nonZeroUnitsMonth === 1)) {
      diff += `${months}${i18n.global.t('generic.dates.month', 2)} `;
   }

   const nonZeroUnitsWeek = [years, Math.floor(months)].filter((unit) => unit > 0).length;
   if (weeks > 0 && (nonZeroUnitsWeek === 0 || nonZeroUnitsWeek === 1)) {
      diff += `${weeks}${i18n.global.t('generic.dates.week', 2)} `;
   }

   const nonZeroUnits = [years, Math.floor(months), Math.floor(weeks)].filter((unit) => unit > 0).length;
   if (days > 0 && (nonZeroUnits === 0 || nonZeroUnits === 1)) {
      diff += `${days}${i18n.global.t('generic.dates.day', 2)} `;
   }

   // Trim any trailing whitespace
   diff = diff.trim();

   return { ...workExperience, diff };
}

export function educationMapper(education: ProfileEducation): any {
   if (!education.from) return education;

   const tillDate = education.till ? dayjs(education.till, 'YYYY-MM-DD') : dayjs();
   const diffObj = dayjs.duration(dayjs(tillDate).diff(dayjs(education.from, 'YYYY-MM-DD')));

   const diffInYears = diffObj.years();
   const diffMonths = diffObj.months();
   const diff =
      diffInYears > 1
         ? `${diffInYears.toFixed(1)}${i18n.global.t('generic.dates.year', 2)}`
         : `${diffMonths.toFixed(0)}${i18n.global.t('generic.dates.month', 2)}`;
   return { ...education, diff };
}
